/**
 * ===========================
 * @author:  am2Studio      ==
 * @package: JS Bundle      ==
 * @version: 2.0.0          ==
 * ===========================
 *
 * @description: {
 * --- @usage {
 * a) plugins we develop for AM2 goes into plugins - reusable stuff
 * b) helpers will contain globally used functions, examples bellow
 * c) routes and its subfolders contains JS that applies for specific block/templates
 * d) vendor contains vendor JS - Should be enqueued with WP or lazyloaded with `loadJs`
 * }
 * --- @structure {
 * a) any plugin we create goes to `/plugins/` folder, should extend main component
 * b) any helper functions goes to `/helpers/` folder (debounce, parseData etc)
 * c) route specific js goes to `/routes/name/index.js` or if its functionality is large
 * you can create multiple files and import them in the index file
 * }
 *
 * @JSManager { The class that handles JS loading/running
 * - common js `init` key runs first
 * - route specific js `init`/`complete`/ keys runs after common (same order)
 * - common js `complete` key runs after
 * - common js `last` key runs last
 * - any other lazyloaded JS runs last of course
 * }
 */

import './../scss/style.scss';

// JSManager + Core Component (used for making JS plugins)
import loadjs from 'loadjs';
import './plugins/am2-Component';
import './plugins/am2-JSManager';
import './plugins/am2-Template';

// Helpers
import helperDebounce from './helpers/debounce';
import helperParseData from './helpers/parseData';
import helperRequireJs from './helpers/requireJs';
import helperCreateParams from './helpers/createParams';
import helperUpdateParams from './helpers/updateParams';
import getScrollBarWidth from './helpers/getScrollBarWidth';

// Common JS
import common from './routes/common';

// Blocks
import blockMap from './routes/blocks/maps';
import blockSlider from './routes/blocks/slider';

/**
 * Routed JS - loaded on specific block/template (if it exists in body class)
 * @see - class-am2-hooks.php @ body_class() filter function
 * @info - each block will generate a class like: am2-{ block-name }
 */

// Attach Some Required Plugins
am2.fn = {};
am2.fn.loadJs = loadjs;
am2.fn.requireJs = helperRequireJs;
am2.fn.debounce = helperDebounce;
am2.fn.getOptions = helperParseData;
am2.fn.createParams = helperCreateParams;
am2.fn.updateParams = helperUpdateParams;
am2.fn.getScrollBarWidth = getScrollBarWidth;

// Attach Specific Route JS
am2 = {
	...am2,
	...{
		lazyJs: {
			/**
			 * @description
			 * Setup JS URLs that are lazy loaded from CDN/Theme with IDs to easly load them later
			 * without using multiple sources and/or npm packages
			 * This helps us to avoid updating multiple files and/or use multiple sources of the same script
			 * @see example under common key and below
			 */
			// Google
			'google': [
				`//maps.googleapis.com/maps/api/js?key=${am2.google.apiKey}`
			],
			// Hubspot
			'hubspot': [
				'//js.hsforms.net/forms/v2.js',
			],
			// AnimeJS
			'anime': [
				'//cdnjs.cloudflare.com/ajax/libs/animejs/3.2.1/anime.min.js'
			],
			// Calendar
			'calendar': [
				'//unpkg.com/@fullcalendar/core@5.1.0/main.js',
			],
			// Select2
			'select2': [
				'//cdn.jsdelivr.net/npm/select2@4.0.13/dist/js/select2.min.js',
				// '//cdn.jsdelivr.net/npm/select2@4.0.13/dist/css/select2.min.css', // See Vendor SCSS
			],
			// MicroModal
			'micromodal': [
				'//unpkg.com/micromodal/dist/micromodal.min.js'
			],
			// Scrollmagic
			'scrollmagic': [
				'//cdnjs.cloudflare.com/ajax/libs/ScrollMagic/2.0.8/ScrollMagic.min.js'
			],
			// Scrollbar
			'scrollbar': [
				'//unpkg.com/smooth-scrollbar@latest/dist/smooth-scrollbar.js'
			],
			// Panorama
			'panorama': [
				'//naver.github.io/egjs-view360/release/3.5.2/dist/view360.pkgd.min.js',
				'//naver.github.io/egjs-view360/common/js/PanoControls.js',
				'//naver.github.io/egjs-view360/common/js/screenfull.min.js',
				// uses jQuery (and we need jQuery instead of $ to avoid replacing WP jQuery)
				`${am2.templateDirectory}/resources/js/vendor/PieView.js`,
				'//naver.github.io/egjs-view360/common/css/PanoControls.css'
			],
			// SweetAlert
			'sweetalert': [
				'//unpkg.com/sweetalert2@11/dist/sweetalert2.min.css',
				'//unpkg.com/sweetalert2@11/dist/sweetalert2.min.js',
			],
			// TippyJS
			'tippy': [
				'//unpkg.com/@popperjs/core@2',
				'//unpkg.com/tippy.js@6',
			],
			// Foundation
			'foundation': [
				'//cdnjs.cloudflare.com/ajax/libs/foundation/6.7.4/js/plugins/foundation.core.min.js',
				'//cdnjs.cloudflare.com/ajax/libs/foundation/6.7.4/js/plugins/foundation.util.keyboard.min.js',
				'//cdnjs.cloudflare.com/ajax/libs/foundation/6.7.4/js/plugins/foundation.util.mediaQuery.min.js',
				'//cdnjs.cloudflare.com/ajax/libs/foundation/6.7.4/js/plugins/foundation.util.imageLoader.min.js',
				'//cdnjs.cloudflare.com/ajax/libs/foundation/6.7.4/js/plugins/foundation.util.motion.min.js',
				'//cdnjs.cloudflare.com/ajax/libs/foundation/6.7.4/js/plugins/foundation.tabs.min.js', 		// Tabs
				'//cdnjs.cloudflare.com/ajax/libs/foundation/6.7.4/js/plugins/foundation.accordion.min.js', // Accordion
				'//cdnjs.cloudflare.com/ajax/libs/foundation/6.7.4/js/plugins/foundation.responsiveAccordionTabs.min.js', // Responsive Accordion/Tabs
			]
		},
		routes: {
			/**
			 * @description
			 * This prop includes the global JS fired on every page
			 * All the js that needs to be loaded everywhere should be placed in `common` folder
			 * There you can split your files into multiple parts based on your needs
			 * @reserved Key
			 */
			common,
			// Map
			blockMap,
			// Sliders
			blockSlider,
		},
	},
};
